import * as React from 'react';

import { Slot } from '@radix-ui/react-slot';
import { type VariantProps, cva } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 aria-disabled:cursor-not-allowed aria-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_[data-icon=inline-start]]:-ml-1 [&_[data-icon=inline-end]]:-mr-1',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground shadow-sm hover:bg-primary-hover active:bg-primary-active',
        destructive:
          'bg-error text-error-foreground shadow-sm hover:bg-error-hover active:bg-error-active',
        outline:
          'border border-input bg-background shadow-xs hover:bg-accent hover:text-accent-foreground',
        secondary:
          'bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        // Extended variants using the project's color tokens
        neutral:
          'bg-neutral text-neutral-foreground shadow-sm hover:bg-neutral-hover active:bg-neutral-active',
        success:
          'bg-success text-success-foreground shadow-sm hover:bg-success-hover active:bg-success-active',
        warning:
          'bg-warning text-warning-foreground shadow-sm hover:bg-warning-hover active:bg-warning-active',
        info: 'bg-info text-info-foreground shadow-sm hover:bg-info-hover active:bg-info-active',
        teal: 'bg-teal text-teal-foreground shadow-sm hover:bg-teal-hover active:bg-teal-active',
        // Outline color variants
        'outline-primary':
          'border-2 border-primary text-primary bg-transparent hover:bg-primary/10',
        'outline-neutral':
          'border-2 border-neutral text-neutral bg-transparent hover:bg-neutral/10',
        'outline-error':
          'border-2 border-error text-error bg-transparent hover:bg-error/10',
        'outline-success':
          'border-2 border-success text-success bg-transparent hover:bg-success/10',
        'outline-warning':
          'border-2 border-warning text-warning bg-transparent hover:bg-warning/10',
        'outline-info':
          'border-2 border-info text-info bg-transparent hover:bg-info/10',
        'outline-teal':
          'border-2 border-teal text-teal bg-transparent hover:bg-teal/10',
        // Ghost color variants
        'ghost-primary':
          'bg-transparent text-primary hover:bg-primary/10 active:bg-primary/20',
        'ghost-neutral':
          'bg-transparent text-foreground hover:bg-neutral/10 active:bg-neutral/20',
        'ghost-error':
          'bg-transparent text-error hover:bg-error/10 active:bg-error/20',
        'ghost-muted':
          'bg-transparent text-muted-foreground hover:bg-muted/10 active:bg-muted/20',
        // Soft color variants
        'soft-primary':
          'bg-primary/20 text-primary hover:bg-primary/30 active:bg-primary/40',
        'soft-neutral':
          'bg-neutral/20 text-neutral hover:bg-neutral/30 active:bg-neutral/40',
        'soft-error':
          'bg-error/20 text-error hover:bg-error/30 active:bg-error/40',
        // Link color variants
        'link-primary':
          'bg-transparent text-primary underline-offset-4 hover:underline',
        'link-neutral':
          'bg-transparent text-foreground underline-offset-4 hover:underline',
        'link-error':
          'bg-transparent text-error underline-offset-4 hover:underline',
        'link-muted':
          'bg-transparent text-muted-foreground underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-10 px-4 py-2 has-[>svg]:px-3',
        xs: 'h-6 rounded px-2 gap-1 text-xs has-[>svg]:px-1.5',
        sm: 'h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'h-12 rounded-md px-6 has-[>svg]:px-4',
        icon: 'size-10',
        'icon-sm': 'size-8',
        'icon-xs': 'size-6',
        'icon-lg': 'size-12',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

function Button({
  className,
  variant = 'default',
  size = 'default',
  asChild = false,
  disabled,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      disabled={disabled}
      aria-disabled={disabled}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
